import React, { useState, useEffect, useRef } from 'react';

export interface SelectorOption {
  title: string;
  description: string;
  image: string;
  icon: React.ReactNode;
}

interface InteractiveSelectorProps {
  options: SelectorOption[];
}

const InteractiveSelector: React.FC<InteractiveSelectorProps> = ({ options }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animatedOptions, setAnimatedOptions] = useState<number[]>([]);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userInteractedRef = useRef(false);

  const handleOptionClick = (index: number) => {
    if (index !== activeIndex) {
      userInteractedRef.current = true;
      setActiveIndex(index);
      // Resume auto-scroll after user interaction pause
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
      autoScrollRef.current = setInterval(() => {
        userInteractedRef.current = false;
        setActiveIndex((prev) => (prev + 1) % options.length);
      }, 3000);
    }
  };

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    options.forEach((_, i) => {
      const timer = setTimeout(() => {
        setAnimatedOptions((prev) => [...prev, i]);
      }, 180 * i);
      timers.push(timer);
    });
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [options]);

  // Auto-scroll: cycle through options every 3 seconds
  useEffect(() => {
    autoScrollRef.current = setInterval(() => {
      if (!userInteractedRef.current) {
        setActiveIndex((prev) => (prev + 1) % options.length);
      }
    }, 3000);

    return () => {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    };
  }, [options.length]);

  return (
    <div className="flex w-full max-w-[1100px] mx-auto h-[440px] md:h-[520px] items-stretch overflow-hidden relative scrollbar-hide">
      {options.map((option, index) => {
        const isActive = activeIndex === index;
        const isAnimated = animatedOptions.includes(index);

        return (
          <div
            key={index}
            className={`relative flex flex-col items-center justify-center overflow-hidden rounded-xl transition-all duration-700 ease-in-out ${isActive ? 'border-2 border-primary-500' : 'border-2 border-background-200/30'}`}
            style={{
              flex: isActive ? '5 1 0%' : '1 1 0%',
              cursor: 'pointer',
              background: 'linear-gradient(180deg, #0d0d0d 0%, #161616 100%)',
              opacity: isAnimated ? 1 : 0,
              transform: isAnimated ? 'translateX(0)' : 'translateX(-60px)',
              transition: 'flex 0.7s ease-in-out, opacity 0.6s ease-out, transform 0.6s ease-out, border-color 0.5s ease',
              margin: '0 4px',
            }}
            onClick={() => handleOptionClick(index)}
          >
            {/* Phone mockup */}
            <div
              className="transition-all duration-700 ease-in-out"
              style={{
                transform: isActive ? 'scale(1) translateY(0)' : 'scale(0.6) translateY(30px)',
                opacity: isAnimated ? (isActive ? 1 : 0.4) : 0,
              }}
            >
              <div className="phone-frame-dark w-[150px] md:w-[220px]">
                <div className="phone-notch-dark" />
                <img
                  src={option.image}
                  alt={option.title}
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Shadow overlay */}
            <div
              className="absolute left-0 right-0 bottom-0 pointer-events-none"
              style={{
                height: '160px',
                background: isActive
                  ? 'linear-gradient(to top, rgba(10,10,10,0.95), transparent)'
                  : 'linear-gradient(to top, rgba(10,10,10,0.8), transparent)',
              }}
            />

            {/* Label */}
            <div className="absolute left-0 right-0 bottom-5 flex items-center gap-3 px-4 z-10">
              <div
                className="min-w-[44px] h-[44px] flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  background: 'rgba(32,32,32,0.85)',
                  border: '2px solid #444',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {option.icon}
              </div>
              <div className="overflow-hidden">
                <div
                  className="font-bold text-lg text-foreground-50 whitespace-pre"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? 'translateX(0)' : 'translateX(20px)',
                    transition: 'all 0.7s ease-in-out',
                  }}
                >
                  {option.title}
                </div>
                <div
                  className="text-sm text-foreground-400 whitespace-pre"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? 'translateX(0)' : 'translateX(20px)',
                    transition: 'all 0.7s ease-in-out 0.05s',
                  }}
                >
                  {option.description}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InteractiveSelector;